import { useExperienceApi } from '@/shared/api/useExperienceApi.ts';
import { useQuery } from '@tanstack/vue-query';
import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import type { GetExperiencesRecommendationsData, GetExperiencesRecommendationsResponse } from '@/shared/api/experience';
import { LISTING_RECOMMENDATIONS_ALGORITHM_FALLBACK_ID } from '@/entities/experience/recommendation/constants';

export const useGetExperiencesRecommendations = (
  params: Ref<GetExperiencesRecommendationsData['query']>,
  enabled: MaybeRef<boolean> = false,
) => {
  const { getExperiencesRecommendations } = useExperienceApi();

  const excludeNeedsLicense = computed(() => {
    return getBootstrapData().hideUnlicensedExperiencesForPartner;
  });

  const {
    data: experiencesData,
    isFetching,
    isFetched,
  } = useQuery({
    queryKey: ['getExperiencesRecommendations', params],
    queryFn: async () => {
      const response = await getExperiencesRecommendations({
        query: {
          ...params.value,
          limit: 0,
          offset: 0,
          extended_movement_types: true,
          exclude_needs_license: excludeNeedsLicense.value,
          photos_count: 1,
        },
      });

      return response.data;
    },
    enabled,
    // Пустой ответ сохраняет форму данных до первой успешной загрузки рекомендаций.
    placeholderData: (prevData): GetExperiencesRecommendationsResponse =>
      prevData || {
        count: 0,
        next: null,
        previous: null,
        results: [],
        recommendation_algorithm_id: LISTING_RECOMMENDATIONS_ALGORITHM_FALLBACK_ID,
        recommendation_algorithm_name: null,
        recommendation_algorithm_type: null,
      },
    retry: false,
  });

  // Метаданные алгоритма рекомендаций используются для SOGU-событий листинга.
  const recommendationAlgorithmMeta = computed(() => {
    const data = experiencesData.value;

    return {
      recommendation_algorithm_id:
        data?.recommendation_algorithm_id?.trim() || LISTING_RECOMMENDATIONS_ALGORITHM_FALLBACK_ID,
      recommendation_algorithm_name: data?.recommendation_algorithm_name ?? undefined,
      recommendation_algorithm_type: data?.recommendation_algorithm_type ?? undefined,
    };
  });

  return {
    experiencesData,
    isFetching,
    isFetched,
    recommendationAlgorithmMeta,
  };
};
